import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { ArtifactViewer } from '../artifacts/ArtifactViewer';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import 'highlight.js/styles/github-dark.css';

// Helper function to identify synthesis artifacts
const isSynthesisArtifact = (artifact) => {
  // Only trust the explicit flags from the backend
  return artifact.is_final_synthesis === true || artifact.type === 'synthesis';
};

// Helper function to identify review artifacts
const isReviewArtifact = (artifact) => {
  // Only trust the explicit flags from the backend
  return artifact.is_review === true || artifact.type === 'review';
};

interface Artifact {
  id: string;
  name: string;
  type: string;
  content: any;
  created_at: string;
  created_by: string;
  persona_id?: string;
  phase_id?: string;
  phase_label?: string;
  is_final_synthesis?: boolean;
  is_review?: boolean;
  tags?: string[];
  _isNew?: boolean;
  _contentFetched?: boolean;
  has_full_content?: boolean;
}

interface IntermediateArtifactsViewerProps {
  artifacts: Artifact[];
  personas: any[];
  onRefresh?: () => void; // Add this prop
}

export const IntermediateArtifactsViewer: React.FC<IntermediateArtifactsViewerProps> = ({
  artifacts,
  personas,
  onRefresh
}) => {
  const { darkMode } = useTheme();
  const [filter, setFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('newest');
  const [selectedArtifact, setSelectedArtifact] = useState<Artifact | null>(null);
  const [filteredArtifacts, setFilteredArtifacts] = useState<Artifact[]>([]);
  const [loadingArtifactId, setLoadingArtifactId] = useState<string | null>(null);
  const [copiedArtifactId, setCopiedArtifactId] = useState<string | null>(null);
  const [personaFilter, setPersonaFilter] = useState<string>('all');
  const [phaseFilter, setPhaseFilter] = useState<string>('all');
  const [tagFilter, setTagFilter] = useState<string>('all');
  
  // Add ref for container to enable scrolling
  const artifactsContainerRef = useRef<HTMLDivElement>(null);
  
  // Add state for refresh loading
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Add a debug log to see what artifacts we have
  useEffect(() => {
    console.log('[ARTIFACTS-DEBUG] IntermediateArtifactsViewer received artifacts:', 
      artifacts.map(a => ({
        id: a.id, 
        type: a.type, 
        is_final: a.is_final_synthesis, 
        name: a.name,
        tags: a.tags
      }))
    );
    
    // Log any synthesis artifacts specifically
    const synthArtifacts = artifacts.filter(a => a.is_final_synthesis || a.type === 'synthesis');
    if (synthArtifacts.length > 0) {
      console.log('[SYNTHESIS-DEBUG] Found synthesis artifacts:', 
        synthArtifacts.map(a => ({
          id: a.id, 
          is_final_synthesis: a.is_final_synthesis, 
          type: a.type,
          name: a.name
        }))
      );
    }
  }, [artifacts]);
  
  // Find synthesis artifacts and review artifacts
  const synthesisArtifacts = artifacts.filter(a => a.is_final_synthesis || a.type === 'synthesis');
  const reviewArtifacts = artifacts.filter(a => a.is_review || a.type === 'review');
  const hasSynthesis = synthesisArtifacts.length > 0;
  const hasReviews = reviewArtifacts.length > 0;
  
  // Log review artifacts for debugging
  useEffect(() => {
    if (reviewArtifacts.length > 0) {
      console.log('Review artifacts found:', reviewArtifacts);
    }
  }, [reviewArtifacts]);
  
  // Get unique persona IDs from artifacts
  const uniquePersonaIds = [...new Set(artifacts.map(a => a.persona_id))].filter(Boolean);
  
  // Extract unique phases, personas, and tags for filtering
  const phases = useMemo(() => {
    const phaseSet = new Set<string>();
    artifacts.forEach(artifact => {
      if (artifact.phase_label) {
        phaseSet.add(artifact.phase_label);
      }
    });
    return Array.from(phaseSet);
  }, [artifacts]);

  const personaIds = useMemo(() => {
    const personaSet = new Set<string>();
    artifacts.forEach(artifact => {
      if (artifact.persona_id) {
        personaSet.add(artifact.persona_id);
      }
    });
    return Array.from(personaSet);
  }, [artifacts]);

  const tags = useMemo(() => {
    const tagSet = new Set<string>();
    artifacts.forEach(artifact => {
      if (artifact.tags) {
        artifact.tags.forEach(tag => tagSet.add(tag));
      }
    });
    return Array.from(tagSet);
  }, [artifacts]);
  
  // Filter and sort artifacts when dependencies change
  useEffect(() => {
    // Filter artifacts based on selected filter
    let filtered = [...artifacts];
    
    if (filter === 'synthesis') {
      filtered = artifacts.filter(a => a.is_final_synthesis || a.type === 'synthesis');
    } else if (filter === 'review') {
      filtered = artifacts.filter(a => a.is_review || a.type === 'review');
    } else if (filter === 'regular') {
      filtered = artifacts.filter(a => 
        !a.is_final_synthesis && a.type !== 'synthesis' && 
        !a.is_review && a.type !== 'review'
      );
    } else if (filter !== 'all') {
      // Filter by type
      filtered = artifacts.filter(a => a.type === filter);
    }
    
    // Apply persona filter
    if (personaFilter !== 'all') {
      filtered = filtered.filter(a => a.persona_id === personaFilter);
    }
    
    // Apply phase filter
    if (phaseFilter !== 'all') {
      filtered = filtered.filter(a => a.phase_label === phaseFilter);
    }
    
    // Apply tag filter
    if (tagFilter !== 'all') {
      filtered = filtered.filter(a => a.tags && a.tags.includes(tagFilter));
    }
    
    // Sort artifacts based on selected sort option
    filtered.sort((a, b) => {
      if (sortBy === 'newest') {
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      } else if (sortBy === 'oldest') {
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      } else if (sortBy === 'name') {
        return a.name.localeCompare(b.name);
      } else if (sortBy === 'type') {
        return a.type.localeCompare(b.type);
      }
      return 0;
    });
    
    setFilteredArtifacts(filtered);
    
    // Only auto-select the first artifact when the filter/sort changes
    // and there's no current selection
    if (!selectedArtifact && filtered.length > 0) {
      setSelectedArtifact(filtered[0]);
    }
  }, [artifacts, filter, sortBy, personaFilter, phaseFilter, tagFilter]); // Add the new filter dependencies
  
  // Listen for artifact updates
  useEffect(() => {
    console.log('[EVENT] Setting up artifact-updated event listener');
    
    const handleArtifactUpdated = (event: CustomEvent) => {
      console.log('[EVENT] artifact-updated event received:', event.detail);
      
      // Force re-render by creating a new array reference
      setFilteredArtifacts([...filteredArtifacts]);
      
      // Scroll to the top where new artifacts appear if this is a new artifact
      if (event.detail?.isNew && artifactsContainerRef.current) {
        artifactsContainerRef.current.scrollTop = 0;
      }
    };
    
    // Also listen for workflow-message events specifically for artifacts
    const handleWorkflowMessage = (event: CustomEvent) => {
      const message = event.detail?.message;
      if (message?.type === 'workflow_artifact_created') {
        console.log('[EVENT] workflow-message artifact created event received:', message.data?.artifact?.id);
        
        // Force re-render by creating a new array reference
        setFilteredArtifacts(prev => {
          // Check if this artifact already exists
          const newArtifact = message.data?.artifact;
          if (newArtifact && !prev.some(a => a.id === newArtifact.id)) {
            console.log('[EVENT] Adding new artifact to filtered artifacts');
            
            // Create a proper copy of the artifact with all required properties
            const processedArtifact = {
              ...newArtifact,
              id: newArtifact.id,
              name: newArtifact.name || 'Unnamed Artifact',
              type: newArtifact.type || 'text',
              content: newArtifact.content,
              created_at: newArtifact.created_at || new Date().toISOString(),
              created_by: newArtifact.created_by,
              persona_id: newArtifact.persona_id,
              is_final_synthesis: Boolean(newArtifact.is_final_synthesis),
              is_review: Boolean(newArtifact.is_review),
              tags: [...(newArtifact.tags || [])],
              _isNew: true
            };
            
            // Log the raw artifact for debugging
            console.log('[SYNTHESIS-DEBUG] Raw artifact in IntermediateArtifactsViewer:', {
              id: newArtifact.id,
              name: newArtifact.name,
              is_final_synthesis: newArtifact.is_final_synthesis,
              type: newArtifact.type
            });
            
            // Check if this is a synthesis artifact based on multiple criteria
            if (isSynthesisArtifact(processedArtifact)) {
              console.log('[SYNTHESIS-DEBUG] Detected synthesis artifact:', processedArtifact.id);
              processedArtifact.is_final_synthesis = true;
              processedArtifact.type = 'synthesis';
              
              if (!processedArtifact.tags) {
                processedArtifact.tags = [];
              }
              if (!processedArtifact.tags.includes('Synthesis')) {
                processedArtifact.tags.push('Synthesis');
              }
            }
            
            // Also check if the name contains "Final Synthesis" as an additional heuristic
            if (processedArtifact.name && processedArtifact.name.toLowerCase().includes('final synthesis')) {
              console.log('[SYNTHESIS-DEBUG] Detected synthesis artifact from name:', processedArtifact.name);
              processedArtifact.is_final_synthesis = true;
              processedArtifact.type = 'synthesis';
              
              if (!processedArtifact.tags) {
                processedArtifact.tags = [];
              }
              if (!processedArtifact.tags.includes('Synthesis')) {
                processedArtifact.tags.push('Synthesis');
              }
            }
            
            // Log the processed artifact for debugging
            console.log('[SYNTHESIS-DEBUG] Processed artifact in IntermediateArtifactsViewer:', {
              id: processedArtifact.id,
              name: processedArtifact.name,
              is_final_synthesis: processedArtifact.is_final_synthesis,
              type: processedArtifact.type,
              tags: processedArtifact.tags
            });
            
            // Check if this is a review artifact
            if (isReviewArtifact(processedArtifact)) {
              console.log('[REVIEW-DEBUG] Detected review artifact:', processedArtifact.id);
              processedArtifact.is_review = true;
              processedArtifact.type = 'review';
              
              if (!processedArtifact.tags) {
                processedArtifact.tags = [];
              }
              if (!processedArtifact.tags.includes('Review')) {
                processedArtifact.tags.push('Review');
              }
            }
            
            // Also check if the name contains "Review" as an additional heuristic
            if (processedArtifact.name && processedArtifact.name.toLowerCase().includes('review by')) {
              console.log('[REVIEW-DEBUG] Detected review artifact from name:', processedArtifact.name);
              processedArtifact.is_review = true;
              processedArtifact.type = 'review';
              
              if (!processedArtifact.tags) {
                processedArtifact.tags = [];
              }
              if (!processedArtifact.tags.includes('Review')) {
                processedArtifact.tags.push('Review');
              }
            }
            
            return [processedArtifact, ...prev];
          }
          return prev;
        });
        
        // Scroll to the top where new artifacts appear
        if (artifactsContainerRef.current) {
          artifactsContainerRef.current.scrollTop = 0;
        }
      }
    };
    
    window.addEventListener('artifact-updated', handleArtifactUpdated as EventListener);
    window.addEventListener('workflow-message', handleWorkflowMessage as EventListener);
    
    return () => {
      console.log('[EVENT] Removing artifact-updated event listener');
      window.removeEventListener('artifact-updated', handleArtifactUpdated as EventListener);
      window.removeEventListener('workflow-message', handleWorkflowMessage as EventListener);
    };
  }, [filteredArtifacts]);
  
  // Add a function to fetch full content
  const fetchFullContent = async (artifact: Artifact) => {
    if (artifact._contentFetched || !artifact.has_full_content) return;
    
    setLoadingArtifactId(artifact.id);
    try {
      // Determine the execution ID from the phase_id
      const executionId = artifact.phase_id?.split('/')[0];
      if (!executionId) {
        console.error('Cannot fetch content: missing execution ID in phase_id');
        return;
      }
      
      console.log(`[ARTIFACT-CONTENT] Fetching content for artifact ${artifact.id} from execution ${executionId}`);
      const response = await fetch(`/api/workflows/executions/${executionId}/artifacts/${artifact.id}`);
      
      if (!response.ok) {
        console.error(`[ARTIFACT-CONTENT] Failed to fetch content: ${response.status} ${response.statusText}`);
        throw new Error('Failed to fetch content');
      }
      
      const fullArtifact = await response.json();
      console.log(`[ARTIFACT-CONTENT] Successfully fetched content for artifact ${artifact.id}, content length: ${fullArtifact.content ? fullArtifact.content.length : 0}`);
      
      // Update the artifact in the list
      setFilteredArtifacts(prev => 
        prev.map(a => a.id === artifact.id ? 
          { ...a, content: fullArtifact.content, _contentFetched: true } : a
        )
      );
      
      // Also update the main artifacts list if this component has access to it
      if (onRefresh) {
        console.log(`[ARTIFACT-CONTENT] Triggering refresh to update main artifacts list`);
        onRefresh();
      }
    } catch (error) {
      console.error('Error fetching artifact content:', error);
    } finally {
      setLoadingArtifactId(null);
    }
  };

  // Function to copy artifact content to clipboard
  const copyArtifactContent = (artifact: Artifact) => {
    let contentToCopy = '';
    
    if (typeof artifact.content === 'string') {
      contentToCopy = artifact.content;
    } else if (artifact.content && typeof artifact.content === 'object') {
      try {
        contentToCopy = JSON.stringify(artifact.content, null, 2);
      } catch (e) {
        contentToCopy = 'Error: Could not stringify content';
      }
    } else {
      contentToCopy = 'No content available';
    }
    
    navigator.clipboard.writeText(contentToCopy);
    setCopiedArtifactId(artifact.id);
    
    // Reset the copied state after 2 seconds
    setTimeout(() => {
      setCopiedArtifactId(null);
    }, 2000);
  };

  // Function to render a preview of the artifact content
  const renderContentPreview = (artifact: Artifact) => {
    if (!artifact.content) {
      return <div className="text-gray-500 italic">No content available</div>;
    }

    if (typeof artifact.content === 'string') {
      // For markdown content types
      if (artifact.type === 'markdown' || 
          artifact.type === 'text' || 
          artifact.type.includes('markdown') || 
          artifact.type.includes('text')) {
        return (
          <div className={`mt-2 p-2 rounded text-xs overflow-hidden max-h-20 ${
            darkMode ? 'bg-gray-700' : 'bg-gray-100'
          }`}>
            <div className="prose prose-sm dark:prose-invert max-w-none overflow-hidden">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeHighlight]}
                className="line-clamp-3"
              >
                {artifact.content.substring(0, 300)}
              </ReactMarkdown>
            </div>
          </div>
        );
      }
      
      // For other string content
      return (
        <div className={`mt-2 p-2 rounded text-xs overflow-hidden max-h-20 ${
          darkMode ? 'bg-gray-700' : 'bg-gray-100'
        }`}>
          {artifact.content.substring(0, 100) + (artifact.content.length > 100 ? '...' : '')}
        </div>
      );
    }
    
    // For object content
    return (
      <div className={`mt-2 p-2 rounded text-xs overflow-hidden max-h-20 ${
        darkMode ? 'bg-gray-700' : 'bg-gray-100'
      }`}>
        Complex content (click to view)
      </div>
    );
  };
  
  const getArtifactTypeColor = (type: string, isFinalSynthesis: boolean = false, isReview: boolean = false, phaseLabel?: string) => {
    // Use the helper functions for more robust detection
    const artifact = { type, is_final_synthesis: isFinalSynthesis, is_review: isReview, phase_label: phaseLabel };
    
    // Check for synthesis first - this should take precedence
    if (isSynthesisArtifact(artifact)) {
      return darkMode ? 'bg-green-800 text-green-200' : 'bg-green-100 text-green-800';
    }
    
    if (isReviewArtifact(artifact)) {
      return darkMode ? 'bg-indigo-800 text-indigo-200' : 'bg-indigo-100 text-indigo-800';
    }
    
    switch (type) {
      case 'text':
        return darkMode ? 'bg-blue-800 text-blue-200' : 'bg-blue-100 text-blue-800';
      case 'image':
        return darkMode ? 'bg-green-800 text-green-200' : 'bg-green-100 text-green-800';
      case 'code':
        return darkMode ? 'bg-yellow-800 text-yellow-200' : 'bg-yellow-100 text-yellow-800';
      case 'error':
        return darkMode ? 'bg-red-800 text-red-200' : 'bg-red-100 text-red-800';
      default:
        return darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800';
    }
  };
  
  return (
    <div className="intermediate-artifacts-viewer">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h3 className={`text-lg font-medium ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
          Committee Artifacts ({artifacts.length})
        </h3>
        
        <div className="flex items-center gap-2">
          {onRefresh && (
            <button
              className={`px-3 py-1 text-sm rounded ${
                darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'
              } text-white mr-2`}
              onClick={() => {
                if (onRefresh) {
                  setIsRefreshing(true);
                  
                  try {
                    const result = onRefresh();
                    
                    // If it returns a promise, handle completion
                    if (result && typeof result.then === 'function') {
                      result.finally(() => {
                        setIsRefreshing(false);
                      });
                    } else {
                      // If not a promise, reset after a short delay
                      setTimeout(() => {
                        setIsRefreshing(false);
                      }, 1000);
                    }
                  } catch (e) {
                    console.error('Error during refresh:', e);
                    setIsRefreshing(false);
                  }
                }
              }}
              disabled={isRefreshing}
            >
              {isRefreshing ? 'Refreshing...' : 'Refresh Artifacts'}
            </button>
          )}
          
          {/* Filter controls - improved layout */}
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className={`px-2 py-1 text-sm rounded border ${
                darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-700'
              }`}
            >
              <option value="all">All Types</option>
              {hasSynthesis && (
                <option value="synthesis">Final Synthesis Only</option>
              )}
              {hasReviews && (
                <option value="review">Reviews Only</option>
              )}
              <option value="regular">Regular Outputs Only</option>
              <option value="text">Text</option>
              <option value="image">Image</option>
              <option value="code">Code</option>
            </select>
            
            <select
              value={personaFilter}
              onChange={(e) => setPersonaFilter(e.target.value)}
              className={`px-2 py-1 text-sm rounded border ${
                darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-700'
              }`}
            >
              <option value="all">All Personas</option>
              {personaIds.map(id => (
                <option key={id} value={id}>{id}</option>
              ))}
            </select>
            
            <select
              value={phaseFilter}
              onChange={(e) => setPhaseFilter(e.target.value)}
              className={`px-2 py-1 text-sm rounded border ${
                darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-700'
              }`}
            >
              <option value="all">All Phases</option>
              {phases.map(phase => (
                <option key={phase} value={phase}>{phase}</option>
              ))}
            </select>
            
            <select
              value={tagFilter}
              onChange={(e) => setTagFilter(e.target.value)}
              className={`px-2 py-1 text-sm rounded border ${
                darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-700'
              }`}
            >
              <option value="all">All Tags</option>
              {tags.map(tag => (
                <option key={tag} value={tag}>{tag}</option>
              ))}
            </select>
            
            {/* Sort dropdown */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className={`px-2 py-1 text-sm rounded border ${
                darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-700'
              }`}
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="name">Name</option>
              <option value="type">Type</option>
            </select>
          </div>
        </div>
      </div>
      
      {selectedArtifact ? (
        <div>
          <div className="mb-4 flex justify-between items-center">
            <button
              className={`px-3 py-1 rounded text-sm ${
                darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'
              }`}
              onClick={() => setSelectedArtifact(null)}
            >
              ← Back to List
            </button>
            
            <button
              className={`px-3 py-1 rounded text-sm flex items-center ${
                darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'
              } text-white`}
              onClick={() => copyArtifactContent(selectedArtifact)}
            >
              {copiedArtifactId === selectedArtifact.id ? (
                <>
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Copied!
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                  </svg>
                  Copy Content
                </>
              )}
            </button>
          </div>
          
          <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white border border-gray-200'}`}>
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-xl font-medium">{selectedArtifact.name}</h2>
              <div className={`text-xs px-2 py-0.5 rounded ${
                selectedArtifact.is_review || selectedArtifact.type === 'review'
                  ? darkMode ? 'bg-purple-700 text-purple-100' : 'bg-purple-200 text-purple-800'
                  : selectedArtifact.is_final_synthesis || selectedArtifact.type === 'synthesis'
                    ? darkMode ? 'bg-green-700 text-green-100' : 'bg-green-200 text-green-800'
                    : darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
              }`}>
                {selectedArtifact.type}
              </div>
            </div>
            
            <div className={`text-sm mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Created by: {selectedArtifact.persona_id || selectedArtifact.created_by} • 
              {new Date(selectedArtifact.created_at).toLocaleString()}
            </div>
            
            <ArtifactViewer artifact={selectedArtifact} />
          </div>
        </div>
      ) : (
        <>
          {filteredArtifacts.length === 0 ? (
            <div className={`p-4 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              No artifacts available
            </div>
          ) : (
            <div 
              ref={artifactsContainerRef}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[600px] overflow-y-auto pr-2"
            >
              <AnimatePresence>
                {filteredArtifacts.map(artifact => (
                  <motion.div
                    key={artifact.id}
                    initial={artifact._isNew ? { scale: 0.8, opacity: 0 } : { scale: 1, opacity: 1 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                    className={`relative p-3 rounded-lg border cursor-pointer ${
                      artifact._isNew ? 'ring-2 ring-blue-500 ring-opacity-75' : ''
                    } ${
                      isSynthesisArtifact(artifact)
                        ? darkMode ? 'bg-green-900 border-green-800 hover:bg-green-800' : 'bg-green-50 border-green-200 hover:bg-green-100'
                        : isReviewArtifact(artifact)
                          ? darkMode ? 'bg-indigo-900 border-indigo-800 hover:bg-indigo-800' : 'bg-indigo-50 border-indigo-200 hover:bg-indigo-100'
                          : darkMode ? 'bg-gray-800 border-gray-700 hover:bg-gray-700' : 'bg-white border-gray-200 hover:bg-gray-50'
                    }`}
                    onClick={() => {
                      // Also update the artifact with synthesis flag if needed
                      if (isSynthesisArtifact(artifact) && !artifact.is_final_synthesis) {
                        artifact.is_final_synthesis = true;
                        artifact.type = 'synthesis';
                      }
                      
                      // Also update the artifact with review flag if needed
                      if (isReviewArtifact(artifact) && !artifact.is_review) {
                        artifact.is_review = true;
                        artifact.type = 'review';
                      }
                      
                      if (artifact.has_full_content && !artifact._contentFetched) {
                        fetchFullContent(artifact);
                      }
                      setSelectedArtifact(artifact);
                    }}
                  >
                    {/* Add a "New" badge for fresh artifacts */}
                    {artifact._isNew && (
                      <div className="absolute top-2 right-2">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                          New
                        </span>
                      </div>
                    )}
                    
                    {/* Add loading indicator when fetching content */}
                    {loadingArtifactId === artifact.id && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 rounded-lg">
                        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                      </div>
                    )}
                    
                    {/* Copy button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent opening the artifact
                        copyArtifactContent(artifact);
                      }}
                      className={`absolute top-2 right-2 p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity ${
                        darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                      } ${artifact._isNew ? 'mr-16' : ''}`}
                      aria-label="Copy content"
                    >
                      {copiedArtifactId === artifact.id ? (
                        <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                        </svg>
                      )}
                    </button>
                    <div className="flex items-center justify-between mb-1">
                      <div className="font-medium truncate">{artifact.name}</div>
                      <div className="flex items-center space-x-1">
                        <div className={`text-xs px-2 py-0.5 rounded ${
                          isSynthesisArtifact(artifact)
                            ? darkMode ? 'bg-green-700 text-green-100' : 'bg-green-200 text-green-800'
                            : isReviewArtifact(artifact)
                              ? darkMode ? 'bg-indigo-700 text-indigo-100' : 'bg-indigo-200 text-indigo-800'
                              : darkMode ? 'bg-gray-700' : 'bg-gray-100'
                        }`}>
                          {artifact.type}
                        </div>
                        {/* Only show Review tag if it's a review but not already showing review type */}
                        {(artifact.is_review && artifact.type !== 'review') && (
                          <div className={`text-xs px-2 py-0.5 rounded ${
                            darkMode ? 'bg-indigo-700 text-indigo-100' : 'bg-indigo-200 text-indigo-800'
                          }`}>
                            Review
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Created by: {artifact.persona_id ? 
                        (personas.find(p => p.id === artifact.persona_id)?.name || artifact.persona_id) : 
                        artifact.created_by}
                    </div>
                    
                    <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {new Date(artifact.created_at).toLocaleString()}
                    </div>
                    
                    {/* Display phase label */}
                    {artifact.phase_label && (
                      <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Phase: {artifact.phase_label}
                      </div>
                    )}
                    
                    {/* Display tags */}
                    {artifact.tags && artifact.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {artifact.tags.map(tag => (
                          <span
                            key={tag}
                            className={`px-1.5 py-0.5 text-xs rounded-full ${
                              darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
                            }`}
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                    
                    {/* Render content preview with markdown support */}
                    {renderContentPreview(artifact)}
                    
                    {/* Add sequential position indicator if available */}
                    {artifact.sequential_position !== undefined && (
                      <div className={`text-xs mt-1 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                        Contribution #{artifact.sequential_position + 1} of {artifact.sequential_total}
                        {artifact.sequential_position > 0 && ' (builds on previous)'}
                      </div>
                    )}
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
        </>
      )}
    </div>
  );
};
